import { toast } from 'sonner'
import { DataProvider } from './DataProvider'
import { Task, TaskFilter, TaskUpdate } from '@/lib/types'
import { supabase } from '@/lib/supabase'
import { activityLogger } from '@/lib/activity-logger'
import { openExternalUrl } from '@/lib/window-utils'
import {
  parseRepeatPattern,
  parseRepeatId,
  calculateNextDate,
  generateRepeatId,
  getTodayString,
} from '@/lib/repeat-utils'
import {
  toggleWorkflowyTaskComplete,
  updateWorkflowyTaskDate,
  updateWorkflowyTaskTime,
  updateWorkflowyTaskName,
  isWorkflowyApiAvailable
} from '@/lib/workflowy-api'
import type { Brian2Settings } from '@/components/Settings'

/**
 * Supabase data provider for webapp
 * Directly accesses Supabase database
 */
export class SupabaseProvider implements DataProvider {
  private userId: string

  constructor(userId: string) {
    this.userId = userId
  }

  async getTasks(filter?: TaskFilter): Promise<Task[]> {
    console.log('[SupabaseProvider] getTasks called for user_id:', this.userId)

    // PHASE 1: Fetch BlockNote tasks from tasks_index (no pagination needed - smaller dataset)
    console.log('[SupabaseProvider] Fetching BlockNote tasks from tasks_index...')
    let indexQuery: any = supabase
      .from('tasks_index')
      .select('task_id, content, user_id, created_at, updated_at, completed, due_date, due_time, duration_min, priority, status, tags, page_id, relations, repeat')
      .eq('user_id', this.userId)

    if (filter?.status) {
      if (filter.status === 'completed') {
        indexQuery = indexQuery.eq('completed', true)
      } else if (filter.status === 'active' || filter.status === 'todo') {
        indexQuery = indexQuery.or('completed.is.null,completed.eq.false')
      } else if (filter.status === 'activeOrCompletedToday') {
        // Lazy loading: open tasks + completed today (avoids loading hundreds of old completed tasks)
        const todayISO = new Date().toISOString().slice(0, 10)
        indexQuery = indexQuery.or(`completed.is.null,completed.eq.false,and(completed.eq.true,updated_at.gte.${todayISO})`)
      }
    }

    const { data: indexTasks, error: indexError } = await indexQuery
      .order('created_at', { ascending: false })

    if (indexError) {
      console.error('[SupabaseProvider] Error fetching tasks_index:', indexError)
    }

    const blocknoteTasks = indexTasks || []
    console.log(`[SupabaseProvider] Loaded ${blocknoteTasks.length} BlockNote tasks`)

    // Enrich BlockNote tasks with project info from nodes table
    const blocknoteTaskIds = new Set(blocknoteTasks.map((t: any) => t.task_id))
    let nodeProjectMap = new Map<string, { project: string | null; projectPath: string | null }>()
    if (blocknoteTaskIds.size > 0) {
      // Batch fetch project info for all BlockNote task IDs
      const taskIdArr = Array.from(blocknoteTaskIds)
      const BATCH = 200
      for (let i = 0; i < taskIdArr.length; i += BATCH) {
        const batch = taskIdArr.slice(i, i + BATCH)
        const { data: nodeRows } = await supabase
          .from('nodes')
          .select('id, properties, page_id')
          .in('id', batch)
        if (nodeRows) {
          for (const row of nodeRows) {
            const props = row.properties || {}
            if (props.project || props.projectPath) {
              nodeProjectMap.set(row.id, {
                project: props.project || null,
                projectPath: props.projectPath || null,
              })
            }
          }
        }
      }
    }

    // Normalize BlockNote tasks to nodes format for transformTask()
    const normalizedBlocknoteTasks = blocknoteTasks.map((t: any) => {
      const projectInfo = nodeProjectMap.get(t.task_id)
      return {
        id: t.task_id,
        content: t.content || 'Untitled task',
        user_id: t.user_id,
        type: 'task',
        created_at: t.created_at,
        updated_at: t.updated_at,
        tags: t.tags || [],
        source: 'blocknote',
        note: null,
        deleted_at: null,
        page_id: t.page_id,
        // Flat columns for compatibility
        completed: t.completed || false,
        due_date: t.due_date,
        due_time: t.due_time,
        duration: t.duration_min,
        priority: t.priority || 0,
        status: t.status || 'todo',
        // Properties JSONB — enriched with project info from nodes
        properties: {
          completed: t.completed || false,
          due_date: t.due_date,
          due_time: t.due_time,
          duration: t.duration_min,
          priority: t.priority || 0,
          status: t.status || 'todo',
          relations: t.relations,
          project: projectInfo?.project || null,
          projectPath: projectInfo?.projectPath || null,
        }
      }
    })

    // PHASE 2: Fetch legacy outliner tasks from nodes (with pagination)
    console.log('[SupabaseProvider] Fetching legacy tasks from nodes...')
    const allNodesTasks: any[] = []
    const batchSize = 1000
    let cursor: { created_at: string; id: string } | null = null
    let hasMore = true

    // Build dedup sets to avoid duplicates between tasks_index and nodes
    // (blocknoteTaskIds already built above for project enrichment)
    // Secondary dedup: page_id + content (handles cases where task_id ≠ node.id)
    const blocknotePageContentKeys = new Set(
      blocknoteTasks.map((t: any) => `${t.page_id}::${(t.content || '').trim().toLowerCase()}`)
    )

    while (hasMore) {
      let query: any = supabase
        .from('nodes')
        .select('id, content, type, tags, parent_id, page_id, position, created_at, updated_at, completed, status, source, source_url, due_date, properties', { count: cursor === null ? 'exact' : undefined })
        .eq('user_id', this.userId)
        .eq('type', 'task')
        .is('deleted_at', null)

      // Apply cursor (only fetch rows older than cursor)
      if (cursor) {
        // Use composite cursor: created_at < X OR (created_at = X AND id < Y)
        // This ensures we get the next page correctly even with duplicate timestamps
        query = query.or(`created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.lt.${cursor.id})`)
      }

      if (filter?.source && filter.source !== 'all') {
        query = query.eq('source', filter.source)
      }

      if (filter?.status) {
        if (filter.status === 'activeOrCompletedToday') {
          const todayISO = new Date().toISOString().slice(0, 10)
          query = query.or(`completed.is.null,completed.eq.false,and(completed.eq.true,updated_at.gte.${todayISO})`)
        } else if (filter.status === 'active' || filter.status === 'todo') {
          query = query.or('completed.is.null,completed.eq.false')
        } else if (filter.status === 'completed') {
          query = query.eq('completed', true)
        } else {
          query = query.eq('status', filter.status)
        }
      }

      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .order('id', { ascending: false }) // Secondary sort for stable cursor
        .limit(batchSize)

      if (error) {
        console.error('[SupabaseProvider] Error fetching nodes tasks:', error)
        break
      }

      if (data && data.length > 0) {
        // Filter out duplicates (tasks that exist in tasks_index)
        // IMPORTANT: Allow completed tasks through even if they exist in tasks_index,
        // because tasks_index can be stale (completed status not synced)
        const uniqueTasks = data.filter((t: any) => {
          if (blocknoteTaskIds.has(t.id) && !t.completed) return false
          // Secondary check: same page + content means same task with different ID
          if (!t.completed) {
            const pageContentKey = `${t.page_id}::${(t.content || '').trim().toLowerCase()}`
            if (blocknotePageContentKeys.has(pageContentKey)) return false
          }
          return true
        })
        allNodesTasks.push(...uniqueTasks)

        // Update cursor to last item in batch
        const lastItem: any = data[data.length - 1]
        cursor = {
          created_at: lastItem.created_at,
          id: lastItem.id
        }

        console.log(`[SupabaseProvider] Batch loaded: ${data.length} nodes tasks (${uniqueTasks.length} unique, total: ${allNodesTasks.length})`)
      }

      // Check if we've fetched all tasks
      hasMore = data && data.length === batchSize

      // Log total count on first batch
      if (cursor && count) {
        console.log(`[SupabaseProvider] Total nodes tasks in database: ${count}`)
      }
    }

    console.log('[SupabaseProvider] Loaded', allNodesTasks.length, 'legacy outliner tasks')

    // PHASE 3: Merge, deduplicate, and transform
    // When same task exists in both tasks_index and nodes, prefer the version
    // with latest updated_at (tasks_index can be stale for completed tasks)
    const merged = [...normalizedBlocknoteTasks, ...allNodesTasks]
    const taskById = new Map<string, any>()
    for (const task of merged) {
      const existing = taskById.get(task.id)
      if (!existing || (task.updated_at || '') > (existing.updated_at || '')) {
        taskById.set(task.id, task)
      }
    }
    const allTasks = Array.from(taskById.values())
    console.log('[SupabaseProvider] Final result:', {
      blocknote: normalizedBlocknoteTasks.length,
      outliner: allNodesTasks.length,
      merged: merged.length,
      deduped: allTasks.length,
      userId: this.userId
    })

    return allTasks.map(row => this.transformTask(row))
  }

  async getTask(taskId: string): Promise<Task | null> {
    const { data, error } = await supabase
      .from('nodes')
      .select('*')
      .eq('id', taskId)
      .eq('user_id', this.userId)
      .eq('type', 'task')
      .is('deleted_at', null)
      .single()

    if (error || !data) {
      return null
    }

    return this.transformTask(data)
  }

  async createTask(task: Partial<Task>): Promise<Task> {
    // Accept both Task.name and Node.content for the task title
    const taskAny = task as any
    const title = task.name || taskAny.content || 'Untitled'
    const pageId = taskAny.page_id || null
    // Merge properties from caller (e.g. quick-add passes fields inside properties object)
    const callerProps = taskAny.properties || {}

    // Resolve fields: check flat camelCase (Task), flat snake_case, then callerProps (from quick-add)
    const dueDate = task.dueDate || taskAny.due_date || callerProps.due_date || null
    const dueTime = task.dueTime || taskAny.due_time || callerProps.due_time || null
    const duration = task.duration || callerProps.duration || null
    const priority = task.priority || callerProps.priority || 0
    const status = task.status || callerProps.status || 'todo'
    const completed = task.completed || callerProps.completed || false
    const repeat = taskAny.repeat || callerProps.repeat || null

    const { data, error } = await supabase
      .from('nodes')
      .insert({
        user_id: this.userId,
        type: 'task',
        content: title,
        tags: task.tags || [],
        source: task.source || 'manual',
        page_id: pageId,
        // Flat columns (indexed, used by views and direct queries)
        completed,
        due_date: dueDate,
        due_time: dueTime,
        duration,
        priority,
        status,
        // Properties JSONB (flexible fields, kept in sync)
        properties: {
          completed,
          due_date: dueDate,
          due_time: dueTime,
          duration,
          status,
          priority,
          repeat,
          assigned_to: task.assignedTo || 'user',
          source_url: task.sourceUrl || null,
          project: task.project || callerProps.project || null,
          projectPath: task.projectPath || callerProps.projectPath || null,
          attachments: task.attachments || [],
        }
      })
      .select()
      .single()

    if (error) {
      throw new Error(`Failed to create task: ${error.message}`)
    }

    const createdTask = this.transformTask(data)

    // Log activity
    await activityLogger.log({
      userId: this.userId,
      actionType: 'task_created',
      entityType: 'task',
      entityId: createdTask.id,
      details: {
        name: createdTask.name,
        source: createdTask.source,
        priority: createdTask.priority,
      },
    })

    return createdTask
  }

  async updateTask(taskId: string, updates: TaskUpdate & Record<string, any>): Promise<boolean> {
    console.log('[SupabaseProvider] updateTask called:', { taskId, updates })

    // Get existing task to compare changes
    const existingTask = await this.getTask(taskId)
    console.log('[SupabaseProvider] Existing task fetched:', existingTask ? { id: existingTask.id, source: existingTask.source } : 'null')

    // Support BOTH TaskUpdate (camelCase) and UpdateNodeInput (snake_case) field names
    const u = updates as any

    // Build properties update object
    const propertyUpdates: any = {}
    const dueDate = u.dueDate ?? u.due_date
    const dueTime = u.dueTime ?? u.due_time
    if (dueDate !== undefined) propertyUpdates.due_date = dueDate
    if (dueTime !== undefined) propertyUpdates.due_time = dueTime
    if (u.duration !== undefined) propertyUpdates.duration = u.duration
    if (u.completed !== undefined) {
      propertyUpdates.completed = u.completed
      // Track when the task was completed/uncompleted
      propertyUpdates.completed_at = u.completed ? new Date().toISOString() : null
    }
    if (u.status !== undefined) propertyUpdates.status = u.status
    if (u.priority !== undefined) propertyUpdates.priority = u.priority
    // Project from TaskUpdate format
    if (u.project !== undefined) propertyUpdates.project = u.project
    if (u.projectPath !== undefined) propertyUpdates.projectPath = u.projectPath

    // Merge properties updates
    const updatePayload: any = {
      updated_at: new Date().toISOString(),
    }
    // Content: TaskUpdate uses 'name', UpdateNodeInput uses 'content'
    if (u.name !== undefined) updatePayload.content = u.name
    if (u.content !== undefined) updatePayload.content = u.content
    if (u.tags !== undefined) updatePayload.tags = u.tags
    // Direct column updates for node fields
    if (dueDate !== undefined) updatePayload.due_date = dueDate
    if (dueTime !== undefined) updatePayload.due_time = dueTime
    if (u.duration !== undefined) updatePayload.duration = u.duration
    if (u.completed !== undefined) updatePayload.completed = u.completed
    if (u.status !== undefined) updatePayload.status = u.status
    if (u.priority !== undefined) updatePayload.priority = u.priority
    if (u.repeat !== undefined) updatePayload.repeat = u.repeat

    // If caller passed a raw 'properties' object, merge it directly
    const rawProperties = u.properties as Record<string, any> | undefined
    if (rawProperties) {
      // Extract known fields from rawProperties into propertyUpdates
      for (const [key, val] of Object.entries(rawProperties)) {
        propertyUpdates[key] = val
      }
    }

    // Merge into properties JSONB
    if (Object.keys(propertyUpdates).length > 0) {
      // Get existing properties (use maybeSingle to avoid 406 on BlockNote tasks)
      const { data: existing } = await supabase
        .from('nodes')
        .select('properties')
        .eq('id', taskId)
        .maybeSingle()

      const existingProps = existing?.properties || {}
      updatePayload.properties = { ...existingProps, ...propertyUpdates }
    }

    // CRITICAL: Set local_modified = true for external source tasks
    // This flag GUARANTEES sync will NEVER overwrite this task until push succeeds
    if (existingTask && existingTask.source && existingTask.source !== 'manual') {
      updatePayload.local_modified = true
      console.log('[SupabaseProvider] Setting local_modified=true for external source task:', taskId)
    }

    // Update Supabase
    const { error } = await supabase
      .from('nodes')
      .update(updatePayload)
      .eq('id', taskId)
      .eq('user_id', this.userId)

    if (error) {
      console.error('[SupabaseProvider] Update failed:', error)
      return false
    }

    console.log('[SupabaseProvider] Supabase update successful')

    // Also sync tasks_index so getTasks() reads consistent data
    const indexPayload: Record<string, any> = { updated_at: updatePayload.updated_at }
    if (updatePayload.completed !== undefined) indexPayload.completed = updatePayload.completed
    if (updatePayload.status !== undefined) indexPayload.status = updatePayload.status
    if (updatePayload.due_date !== undefined) indexPayload.due_date = updatePayload.due_date
    if (updatePayload.due_time !== undefined) indexPayload.due_time = updatePayload.due_time
    if (updatePayload.duration !== undefined) indexPayload.duration_min = updatePayload.duration
    if (updatePayload.priority !== undefined) indexPayload.priority = updatePayload.priority
    if (updatePayload.tags !== undefined) indexPayload.tags = updatePayload.tags
    if (updatePayload.content !== undefined) indexPayload.content = updatePayload.content
    if (updatePayload.repeat !== undefined) indexPayload.repeat = updatePayload.repeat

    if (Object.keys(indexPayload).length > 1) {
      // Try primary key match first
      const { data: indexRows, error: indexError } = await supabase
        .from('tasks_index')
        .update(indexPayload)
        .eq('task_id', taskId)
        .eq('user_id', this.userId)
        .select('task_id')

      if (indexError) {
        console.warn('[SupabaseProvider] tasks_index sync failed (non-fatal):', indexError.message)
      } else if (!indexRows || indexRows.length === 0) {
        // Fallback: task_id in tasks_index might differ from nodes.id
        // Try matching by page_id + content (compound key)
        const { data: nodeRow } = await supabase
          .from('nodes')
          .select('page_id, content')
          .eq('id', taskId)
          .maybeSingle()

        if (nodeRow?.page_id && nodeRow?.content) {
          const { error: fallbackError } = await supabase
            .from('tasks_index')
            .update(indexPayload)
            .eq('page_id', nodeRow.page_id)
            .eq('user_id', this.userId)
            .ilike('content', nodeRow.content.trim())

          if (fallbackError) {
            console.warn('[SupabaseProvider] tasks_index fallback sync failed:', fallbackError.message)
          } else {
            console.log('[SupabaseProvider] tasks_index synced via page_id+content fallback')
          }
        }
      } else {
        console.log('[SupabaseProvider] tasks_index synced successfully')
      }
    }

    // Push changes back to source (2-way sync)
    // BUT: Skip if user is disconnecting from source (changing to 'manual')
    if (updates.source === 'manual') {
      console.log('[SupabaseProvider] Task disconnected from source (now manual) - skipping 2-way sync')
    } else if (existingTask) {
      console.log('[SupabaseProvider] Calling syncChangesToSource...')
      await this.syncChangesToSource(existingTask, updates)
    } else {
      console.warn('[SupabaseProvider] Skipping syncChangesToSource - existingTask is null')
    }

    // Determine action type based on what changed
    let actionType: 'task_updated' | 'task_completed' | 'task_uncompleted' = 'task_updated'
    if (existingTask && updates.completed !== undefined) {
      if (updates.completed && !existingTask.completed) {
        actionType = 'task_completed'

        // Handle repeating tasks: create next instance when marking as done
        await this.handleRepeatTask(existingTask)
      } else if (!updates.completed && existingTask.completed) {
        actionType = 'task_uncompleted'
      }
    }

    // Log activity with human-readable change descriptions
    await activityLogger.log({
      userId: this.userId,
      actionType,
      entityType: 'task',
      entityId: taskId,
      details: {
        name: updates.name || existingTask?.name,
        changes: describeTaskChanges(updates),
      },
    })

    return true
  }

  /**
   * Sync task changes back to original source (2-way sync)
   */
  private async syncChangesToSource(existingTask: Task, updates: TaskUpdate): Promise<void> {
    const taskId = existingTask.id
    const source = existingTask.source

    console.log('[SupabaseProvider] syncChangesToSource called:', { taskId, source, updates })

    // Only sync if this is a task from an external source
    if (source === 'workflowy' && taskId.startsWith('wf-')) {
      const workflowyAvailable = await isWorkflowyApiAvailable()
      if (!workflowyAvailable) {
        console.log('[SupabaseProvider] Workflowy API not configured, skipping 2-way sync')
        return
      }

      // Sync completion status
      if (updates.completed !== undefined && updates.completed !== existingTask.completed) {
        await toggleWorkflowyTaskComplete(taskId, updates.completed)
      }

      // Sync date changes
      if (updates.dueDate !== undefined && updates.dueDate !== existingTask.dueDate) {
        await updateWorkflowyTaskDate(taskId, updates.dueDate)
      }

      // Sync time changes
      if (updates.dueTime !== undefined && updates.dueTime !== existingTask.dueTime) {
        await updateWorkflowyTaskTime(taskId, updates.dueTime)
      }

      // Sync name changes
      if (updates.name !== undefined && updates.name !== existingTask.name) {
        await updateWorkflowyTaskName(taskId, updates.name)
      }

      console.log('[SupabaseProvider] Synced changes back to Workflowy:', taskId)

      // Clear local_modified flag since push succeeded
      await this.clearLocalModifiedFlag(taskId)
    }
    // Obsidian: Already works via Obsidian plugin listening to Supabase changes
  }

  /**
   * Clear local_modified flag after successful push to external source
   * This allows sync to update this task again
   */
  private async clearLocalModifiedFlag(taskId: string): Promise<void> {
    const { error } = await supabase
      .from('nodes')
      .update({ local_modified: false })
      .eq('id', taskId)
      .eq('user_id', this.userId)

    if (error) {
      console.error('[SupabaseProvider] Failed to clear local_modified flag:', error)
    } else {
      console.log('[SupabaseProvider] Cleared local_modified flag for:', taskId)
    }
  }

  /**
   * Handle repeating tasks: create next instance when task is marked as done
   */
  private async handleRepeatTask(task: Task): Promise<void> {
    const pattern = parseRepeatPattern(task)
    if (!pattern) return // Not a repeating task

    console.log('[SupabaseProvider] Repeating task detected:', pattern)

    // Get current date from task or use today
    const currentDate = task.dueDate || getTodayString()
    const nextDate = calculateNextDate(currentDate, pattern)

    if (!nextDate) {
      console.error('[SupabaseProvider] Could not calculate next date for pattern:', pattern)
      return
    }

    // Get or generate repeat ID
    let repeatId = parseRepeatId(task)
    if (!repeatId) {
      repeatId = generateRepeatId()
      console.log('[SupabaseProvider] Generated new repeat ID:', repeatId)

      // Update original task with repeat ID (in properties JSONB)
      await supabase
        .from('nodes')
        .update({
          properties: {
            ...task.metadata, // Preserve existing properties
            repeat_id: repeatId,
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', task.id)
        .eq('user_id', this.userId)
    }

    // Create next instance
    const nextTask: Partial<Task> = {
      name: task.name,
      rawName: task.rawName,
      dueDate: nextDate,
      dueTime: task.dueTime,
      duration: task.duration,
      completed: false, // New instance starts as TODO
      status: 'todo',
      priority: task.priority,
      tags: task.tags,
      repeat: task.repeat,
      repeatId: repeatId,
      project: task.project,
      projectPath: task.projectPath,
      source: task.source,
      sourceUrl: task.sourceUrl,
    }

    const { data, error } = await supabase
      .from('nodes')
      .insert({
        user_id: this.userId,
        type: 'task',
        content: nextTask.name || 'Untitled',
        tags: nextTask.tags || [],
        source: nextTask.source || 'obsidian',
        properties: {
          completed: false,
          due_date: nextTask.dueDate,
          due_time: nextTask.dueTime,
          duration: nextTask.duration,
          status: 'todo',
          priority: nextTask.priority || 0,
          repeat: nextTask.repeat,
          repeat_id: repeatId,
          project: nextTask.project,
          project_path: nextTask.projectPath,
          source_url: nextTask.sourceUrl,
        },
      })
      .select()
      .single()

    if (error) {
      console.error('[SupabaseProvider] Error creating next repeat instance:', error)
      return
    }

    console.log('[SupabaseProvider] Created next repeat instance:', {
      id: data.id,
      dueDate: nextDate,
      repeatId,
    })

    // Log activity
    await activityLogger.log({
      userId: this.userId,
      actionType: 'task_created',
      entityType: 'task',
      entityId: data.id,
      details: {
        name: nextTask.name,
        source: nextTask.source,
        repeat: true,
        repeatId,
        previousTaskId: task.id,
      },
    })
  }

  async deleteTask(taskId: string): Promise<boolean> {
    // Get task details before deletion
    const task = await this.getTask(taskId)

    const { error } = await supabase
      .from('nodes')
      .delete()
      .eq('id', taskId)
      .eq('user_id', this.userId)

    if (error) return false

    // Log activity
    if (task) {
      await activityLogger.log({
        userId: this.userId,
        actionType: 'task_deleted',
        entityType: 'task',
        entityId: taskId,
        details: {
          name: task.name,
          source: task.source,
        },
      })
    }

    return true
  }

  async navigateToTask(taskId: string): Promise<void> {
    console.log('[SupabaseProvider] navigateToTask called for taskId:', taskId)
    const task = await this.getTask(taskId)

    if (!task) {
      console.error('[SupabaseProvider] Task not found:', taskId)
      toast.error('Task hittades inte!')
      return
    }

    console.log('[SupabaseProvider] Task found:', {
      id: task.id,
      name: task.name,
      source: task.source,
      sourceUrl: task.sourceUrl,
      hasSourceUrl: !!task.sourceUrl
    })

    if (task.sourceUrl) {
      // Check if this is a fallback search URL (Obsidian or Roam)
      if ((task.source === 'obsidian' || task.source === 'roam') && task.sourceUrl.includes('search')) {
        const sourceName = task.source === 'obsidian' ? 'Obsidian' : 'Roam'
        toast.warning(`Öppnar ${sourceName}-sökning (fallback-URL)`, {
          description: `Synca om tasks med korrekt source_url för direktlänk.`,
        })
      }

      console.log('[SupabaseProvider] Opening sourceUrl:', task.sourceUrl)
      openExternalUrl(task.sourceUrl)
    } else {
      // No sourceUrl - silently log, don't bother user with alert
      console.log('[SupabaseProvider] No sourceUrl for task:', task.name, '(source:', task.source, ')')
    }
  }

  async getActiveSources(): Promise<string[]> {
    const { data } = await supabase
      .from('nodes')
      .select('source')
      .eq('user_id', this.userId)
      .eq('type', 'task')

    if (!data) return []

    const sources = new Set(data.map(row => row.source))
    return Array.from(sources)
  }

  async getSetting<T>(key: string): Promise<T | null> {
    try {
      // Try to get from Supabase first
      const { data, error } = await supabase
        .from('user_settings')
        .select('value')
        .eq('user_id', this.userId)
        .eq('key', key)
        .maybeSingle()

      if (error) {
        console.error('[SupabaseProvider] Error fetching setting:', error)
        // Fallback to localStorage - use same key format as settings-storage.ts
        const storageKey = key === 'brian2_settings'
          ? `taskSidekickSettings:${this.userId}`
          : `brian2:${key}`
        const stored = localStorage.getItem(storageKey)
        return stored ? JSON.parse(stored) : null
      }

      if (data?.value) {
        return data.value as T
      }

      // If not in database, check localStorage for migration
      // Use same key format as settings-storage.ts
      const storageKey = key === 'brian2_settings'
        ? `taskSidekickSettings:${this.userId}`
        : `brian2:${key}`
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const value = JSON.parse(stored) as T
        // Migrate to database
        await this.setSetting(key, value)
        return value
      }

      return null
    } catch (error) {
      console.error('[SupabaseProvider] Exception in getSetting:', error)
      return null
    }
  }

  async setSetting<T>(key: string, value: T): Promise<void> {
    try {
      // Save to Supabase with upsert (insert or update)
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: this.userId,
          key,
          value: value as any,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,key'
        })

      if (error) {
        console.error('[SupabaseProvider] Error saving setting:', error)
        // Fallback to localStorage
        localStorage.setItem(`brian2:${key}`, JSON.stringify(value))
        throw error
      }

      // Also keep in localStorage as cache/backup
      localStorage.setItem(`brian2:${key}`, JSON.stringify(value))
    } catch (error) {
      console.error('[SupabaseProvider] Exception in setSetting:', error)
      // Ensure it's at least saved to localStorage
      localStorage.setItem(`brian2:${key}`, JSON.stringify(value))
      throw error
    }
  }

  private transformTask(row: any): Task {
    // Merge flat columns and properties JSONB (flat columns take priority when non-null)
    const props = row.properties || {}

    return {
      id: row.id,
      name: row.content,
      rawName: props.raw_name,
      page_id: row.page_id || null,
      note: row.note,
      dueDate: row.due_date ?? props.due_date,
      dueTime: row.due_time ?? props.due_time,
      duration: row.duration ?? props.duration,
      completed: row.completed ?? props.completed ?? false,
      status: row.status ?? props.status ?? 'todo',
      priority: row.priority ?? props.priority ?? 0,
      tags: row.tags || [],
      repeat: props.repeat,
      repeatId: props.repeat_id,
      project: props.project,
      projectPath: props.projectPath || props.project_path,
      source: row.source,
      url: props.url || props.source_url,
      sourceUrl: props.source_url,
      assignedTo: props.assigned_to || 'user',
      isCalendarEvent: props.is_calendar_event || false,
      calendarId: props.calendar_id,
      eventId: props.event_id,
      calendarName: props.calendar_name,
      reminders: props.reminders || [],
      reminderMinutes: props.reminder_minutes,
      attachments: props.attachments || [],
      metadata: {
        ...(row.metadata || {}),
        updated_at: row.updated_at || '',
        created_at: row.created_at || '',
      },
    }
  }
}

// ============================================
// Helpers
// ============================================

const STATUS_LABELS: Record<string, string> = {
  todo: 'Att göra',
  inProgress: 'Pågående',
  done: 'Klar',
  cancelled: 'Avbruten',
}

const PRIORITY_LABELS: Record<number, string> = {
  0: 'Ingen',
  1: 'Låg (!)',
  2: 'Medium (!!)',
  3: 'Hög (!!!)',
}

/** Build human-readable change descriptions for activity log */
function describeTaskChanges(updates: Record<string, any>): string[] {
  const descriptions: string[] = []
  const u = updates as any

  // Status / completion
  if (u.status !== undefined) {
    descriptions.push(`Status → ${STATUS_LABELS[u.status] || u.status}`)
  } else if (u.completed !== undefined) {
    descriptions.push(u.completed ? 'Markerad som klar' : 'Markerad som ej klar')
  }

  // Due date
  const dueDate = u.dueDate ?? u.due_date
  if (dueDate !== undefined) {
    descriptions.push(dueDate ? `Datum → ${dueDate}` : 'Datum borttaget')
  }

  // Due time
  const dueTime = u.dueTime ?? u.due_time
  if (dueTime !== undefined) {
    descriptions.push(dueTime ? `Tid → ${dueTime}` : 'Tid borttagen')
  }

  // Priority
  if (u.priority !== undefined) {
    descriptions.push(`Prioritet → ${PRIORITY_LABELS[u.priority] ?? u.priority}`)
  }

  // Duration
  if (u.duration !== undefined) {
    descriptions.push(u.duration ? `Varaktighet → ${u.duration} min` : 'Varaktighet borttagen')
  }

  // Tags
  if (u.tags !== undefined) {
    descriptions.push(`Taggar → ${Array.isArray(u.tags) ? u.tags.join(', ') : u.tags}`)
  }

  // Content / name
  if (u.name !== undefined || u.content !== undefined) {
    descriptions.push('Namn ändrat')
  }

  // Repeat
  if (u.repeat !== undefined) {
    descriptions.push(u.repeat ? `Upprepa → ${u.repeat}` : 'Upprepning borttagen')
  }

  // Project (from properties)
  if (u.properties?.project !== undefined || u.project !== undefined) {
    const projectName = u.properties?.projectPath || u.projectPath
    descriptions.push(projectName ? `Projekt → ${projectName}` : 'Projekt borttaget')
  }

  // Note
  if (u.note !== undefined) {
    descriptions.push('Anteckning ändrad')
  }

  // Fallback: if nothing matched, list raw keys (excluding internal fields)
  if (descriptions.length === 0) {
    const skip = new Set(['source', 'local_modified'])
    const keys = Object.keys(updates).filter(k => !skip.has(k))
    if (keys.length > 0) descriptions.push(keys.join(', '))
  }

  return descriptions
}
